import type { EngineOverrides } from '@/api/generated/types.gen';

/**
 * Per-run "escape hatch" override keys: opt-in levers a user flips for a single
 * mesh run. Each has a fresh-store default (used when nothing was saved).
 *
 * `bypass_dirty_cad_gates` is the dirty-CAD pre-flight opt-out. It defaults ON
 * for every pipeline (matching the fresh-store seed in `toolSlice`), and the
 * user's last submitted / drafted value is now RESTORED on rehydrate rather
 * than reset — reopening a project surfaces the setting the last mesh actually
 * ran with, so an explicit un-tick survives a refresh.
 */
// Per-run key → its fresh-store default, and the single source of truth for
// those defaults: `toolSlice` spreads this as its fresh-store seed, and
// `restorePerRunOverrides` fills it in only for keys a saved source didn't
// carry — so adding a key can't silently drop it, and the seed can't drift
// from the fallback value.
export const PER_RUN_OVERRIDE_DEFAULTS = {
  bypass_dirty_cad_gates: true,
} as const satisfies Partial<EngineOverrides>;

/**
 * A copy of `overrides` with the user's saved per-run escape-hatch values
 * preserved — restore whatever was submitted / drafted, and fall back to the
 * fresh-store default only for a per-run key the saved source left absent or
 * null (an older mesh record predating the field, or a stale "unset"
 * sentinel). Non-per-run keys pass through untouched.
 */
export function restorePerRunOverrides(
  overrides: EngineOverrides,
): EngineOverrides {
  const restored: EngineOverrides = { ...overrides };
  for (const [key, def] of Object.entries(PER_RUN_OVERRIDE_DEFAULTS)) {
    const k = key as keyof typeof PER_RUN_OVERRIDE_DEFAULTS;
    if (restored[k] == null) restored[k] = def;
  }
  return restored;
}
